import { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import { clearToken } from 'utils/axios';
import { openSnackbar } from 'api/snackbar';

interface SessionTimerProps {
  expiresIn: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  variant?: 'default' | 'compact';
}

function totalSecondsFromParts(p: { hours: number; minutes: number; seconds: number }) {
  return p.hours * 3600 + p.minutes * 60 + p.seconds;
}

function partsFromTotalSeconds(remainingSeconds: number) {
  const s = Math.max(0, remainingSeconds);
  return {
    hours: Math.floor(s / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60
  };
}

export default function SessionTimer({ expiresIn, variant = 'default' }: SessionTimerProps) {
  const theme = useTheme();
  /** Instantâneo de expiração da sessão (ms desde epoch), derivado do último `expiresIn` recebido. */
  const deadlineMsRef = useRef(0);

  const [timeLeft, setTimeLeft] = useState(() => partsFromTotalSeconds(totalSecondsFromParts(expiresIn)));

  const warningShown = useRef(false);
  const criticalShown = useRef(false);

  // Ancora o fim da sessão ao relógio real (abas em background não “atrasam” mais o tempo mostrado).
  useEffect(() => {
    const budget = totalSecondsFromParts(expiresIn);
    deadlineMsRef.current = Date.now() + budget * 1000;

    const remaining = Math.max(0, Math.floor((deadlineMsRef.current - Date.now()) / 1000));
    if (remaining > 600) {
      warningShown.current = false;
    }
    if (remaining > 120) {
      criticalShown.current = false;
    }
    setTimeLeft(partsFromTotalSeconds(remaining));
  }, [expiresIn]);

  useEffect(() => {
    const tick = () => {
      const remainingSeconds = Math.max(0, Math.floor((deadlineMsRef.current - Date.now()) / 1000));

      if (remainingSeconds <= 0) {
        clearToken();
        window.location.href = '/login';
        return;
      }

      if (remainingSeconds <= 600 && !warningShown.current) {
        warningShown.current = true;
        openSnackbar({
          open: true,
          message: '⚠️ Atenção! Sua sessão expira em 10 minutos. Salve suas alterações para não perdê-las.',
          variant: 'alert',
          alert: {
            color: 'warning',
            variant: 'filled'
          },
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        } as any);
      }

      if (remainingSeconds <= 120 && !criticalShown.current) {
        criticalShown.current = true;
        openSnackbar({
          open: true,
          message: '🚨 URGENTE! Sua sessão expira em 2 minutos. Salve suas alterações imediatamente!',
          variant: 'alert',
          alert: {
            color: 'error',
            variant: 'filled'
          },
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        } as any);
      }

      setTimeLeft(partsFromTotalSeconds(remainingSeconds));
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresIn]);

  // No app desktop a sessão é longa (7 dias) — o contador perde sentido; esconde.
  // (Depois de todos os hooks, para não violar as regras de hooks.)
  if (typeof window !== 'undefined' && (window as unknown as { desktop?: { isElectron?: boolean } }).desktop?.isElectron) {
    return null;
  }

  const budgetSeconds = Math.max(1, totalSecondsFromParts(expiresIn));
  const currentSeconds = timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds;
  const progressPercentage = Math.max(0, Math.min(100, (currentSeconds / budgetSeconds) * 100));

  const getColor = () => {
    const totalSeconds = timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds;

    if (totalSeconds <= 120) return theme.palette.error.main;
    if (totalSeconds <= 600) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const formatTime = () => {
    if (timeLeft.hours > 0) {
      return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    }
    if (timeLeft.minutes > 0) {
      return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
    }
    return `${timeLeft.seconds}s`;
  };

  const isCritical = timeLeft.hours * 3600 + timeLeft.minutes * 60 + timeLeft.seconds <= 120;

  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          flexShrink: 0
        }}
      >
        <ClockCircleOutlined
          style={{
            color: getColor(),
            fontSize: 14,
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: isCritical ? getColor() : theme.palette.text.primary,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        >
          {formatTime()}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ClockCircleOutlined
          style={{
            color: getColor(),
            fontSize: 16,
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            color: isCritical ? getColor() : 'text.secondary',
            animation: isCritical ? 'blink 1s infinite' : 'none'
          }}
        >
          Sessão expira em: {formatTime()}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        sx={{
          height: 3,
          borderRadius: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getColor(),
            borderRadius: 1.5,
            animation: isCritical ? 'pulse 1s infinite' : 'none'
          },
          '@keyframes blink': {
            '0%, 50%': { opacity: 1 },
            '51%, 100%': { opacity: 0.5 }
          },
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.7 },
            '100%': { opacity: 1 }
          }
        }}
      />
    </Box>
  );
}
